/**
 * 인앱 상품(IAP) 데이터 — `data/iap.tsv` → `generated/iap.json` 재구성. 상품 정의(SKU/가격/라벨)를
 * 코드가 아닌 데이터로 제어(no-hardcode). Google Play Console 인앱상품 ID = `googlePlaySku`와 일치시킨다.
 * 수치/상품은 `data/iap.tsv` 수정 후 `npm run export:data`로 재생성한다.
 */
import iapJson from "./generated/iap.json";
import type { ProductKind } from "@yjsgames/payment-ports";

/** IAP 상품 1종 — 결제 식별(productId/sku) + 참고 가격 + 보상 종류 + i18n 라벨. */
export interface IapProduct {
  readonly productId: string;
  /** Google Play Console 인앱상품 ID (결제 시 사용). 보통 productId와 동일. */
  readonly googlePlaySku: string;
  readonly kind: ProductKind;
  /** 현금 가격(USD) — 표시/참고용. 실제 청구액은 스토어가 지역 통화로 결정. */
  readonly priceUsd: number;
  /** 레거시 폴백 가격 필드. 광고 제거 IAP는 스토어 전용이므로 0이어야 한다. */
  readonly diamondFallbackPrice: number;
  /** 보상 종류 — 현재 adFree(광고 제거)만. 향후 diamond 등 확장. */
  readonly rewardKind: "adFree";
  /** 구매 성공 시 추가로 1회 지급하는 보너스 다이아(0=없음). 패키지 상품(광고제거+다이아)용. */
  readonly bonusDiamond: number;
  readonly nameKey: string;
  readonly descKey: string;
}

/** 광고 제거(단독) 상품 productId. 상점 단독 구매 버튼·기존 호출부 호환용. */
export const REMOVE_ADS_PRODUCT_ID = "remove_ads";
/** 광고 제거 + 다이아 패키지 상품 productId. */
export const REMOVE_ADS_PACKAGE_PRODUCT_ID = "remove_ads_package";

export const IAP_PRODUCTS: readonly IapProduct[] = iapJson.map((row) => ({
  productId: row.productId,
  googlePlaySku: row.googlePlaySku,
  kind: row.kind as ProductKind,
  priceUsd: row.priceUsd,
  diamondFallbackPrice: row.diamondFallbackPrice,
  rewardKind: row.rewardKind as "adFree",
  bonusDiamond: row.bonusDiamond,
  nameKey: row.nameKey,
  descKey: row.descKey,
}));

/**
 * 광고 제거(adFree) entitlement를 부여하는 모든 productId 집합 — 구매/복원 판정 SSoT.
 * 단독·패키지 어느 쪽을 사도 adFree=true가 되며, 둘 다 가진 의미는 없다(상호배타: adFree면 둘 다 구매 차단).
 */
export const AD_FREE_PRODUCT_IDS: readonly string[] = IAP_PRODUCTS.filter(
  (p) => p.rewardKind === "adFree",
).map((p) => p.productId);

/** 광고 제거 entitlement를 부여하는 네이티브 스토어 SKU 집합 — 복원 결과 매핑용. */
export const AD_FREE_STORE_PRODUCT_IDS: readonly string[] = Array.from(
  new Set(
    IAP_PRODUCTS.filter((p) => p.rewardKind === "adFree").flatMap((p) => [
      p.productId,
      p.googlePlaySku,
    ]),
  ),
);

export function iapProductById(id: string): IapProduct | undefined {
  return IAP_PRODUCTS.find((p) => p.productId === id);
}

/** productId 또는 네이티브 스토어 SKU가 광고 제거 entitlement를 부여하는가(복원 백필 판정). */
export function grantsAdFree(id: string): boolean {
  return AD_FREE_PRODUCT_IDS.includes(id) || AD_FREE_STORE_PRODUCT_IDS.includes(id);
}

/** 내부 productId → 네이티브 스토어 조회용 SKU. */
export function nativeStoreProductIdOf(id: string): string {
  return iapProductById(id)?.googlePlaySku || id;
}

/** productId → 레거시 다이아 폴백 가격. 광고 제거 IAP는 스토어 전용이라 0. */
export function diamondFallbackPriceOf(id: string): number {
  return iapProductById(id)?.diamondFallbackPrice ?? 0;
}

/** productId → 구매 성공 시 지급할 보너스 다이아(없으면 0). 패키지 상품 1회 지급용. */
export function bonusDiamondOf(id: string): number {
  return iapProductById(id)?.bonusDiamond ?? 0;
}
